import React, { useEffect, useState } from "react";
import logo from "../assets/image/eblackdog_logo.png";

const API_BASE_URL = "https://patrol-react.eblackdog.net//api";

export default function GuardLogin() {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    loginType: "sentinel",
    remember: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [status, setStatus] = useState({
    visible: false,
    message: "Ready to login",
    type: "info",
  });

  const showStatus = (message, type = "info") => {
    setStatus({ visible: true, message, type });
  };

  useEffect(() => {
    const rememberedUsername = localStorage.getItem("guard_remembered_username");
    const rememberedType = localStorage.getItem("guard_remembered_type");

    if (rememberedUsername) {
      setFormData((prev) => ({
        ...prev,
        username: rememberedUsername,
        loginType: rememberedType || "sentinel",
        remember: true,
      }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const clearOldAdminLoginData = () => {
    localStorage.removeItem("company_name");
    localStorage.removeItem("licensedFor");
    localStorage.removeItem("admin_active");
    localStorage.removeItem("company_address");
    localStorage.removeItem("company_phone");
    localStorage.removeItem("admin_email");
    localStorage.removeItem("company_logo");
    localStorage.removeItem("user_map");
  };

  const startGpsOnLogin = async (token) => {
    if (!navigator.geolocation) return;

    const position = await new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        () => resolve(null),
        {
          enableHighAccuracy: true,
          timeout: 8000,
          maximumAge: 0,
        }
      );
    });

    if (!position?.coords) return;

    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;
    const accuracy = position.coords.accuracy || 0;

    if (
      Number.isNaN(latitude) ||
      Number.isNaN(longitude) ||
      latitude < -90 ||
      latitude > 90 ||
      longitude < -180 ||
      longitude > 180
    ) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/gps`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: "ensure",
          latitude,
          longitude,
          accuracy,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (data?.success && data?.tracking_id) {
        sessionStorage.setItem("gps_tracking_id", String(data.tracking_id));
      }
    } catch (error) {
      console.error("[GuardLogin] GPS start error:", error);
    }
  };

  const getLoginApi = () => {
    if (formData.loginType === "patrol") {
      return `${API_BASE_URL}/guard-login/login`;
    }

    return `${API_BASE_URL}/guard-login/login`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username.trim() || !formData.password.trim()) {
      showStatus("Please enter username and password", "error");
      return;
    }

    setLoading(true);
    showStatus("Authenticating guard credentials...", "info");

    try {
      const res = await fetch(getLoginApi(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          username: formData.username.trim(),
          password: formData.password,
          loginType: formData.loginType,
        }),
      });

      const result = await res.json().catch(() => ({}));

      if (!res.ok || !result.success) {
        showStatus(result.message || "Guard login failed", "error");
        setLoading(false);
        return;
      }

      const token = result.token;
      const rawGuard = result.data || result.guard || result.user || {};

      if (!token) {
        showStatus("Login success but token missing from backend", "error");
        setLoading(false);
        return;
      }

      const memberId =
        rawGuard.memberId ||
        rawGuard.member_id ||
        rawGuard.fld_memberId ||
        rawGuard.guard_id ||
        formData.username.trim();

      const adminId =
        rawGuard.adminId ||
        rawGuard.admin_id ||
        rawGuard.created_by_admin_id ||
        rawGuard.createdByAdminId ||
        rawGuard.created_by ||
        null;

      const designation =
        rawGuard.designation ||
        rawGuard.fld_designation ||
        (formData.loginType === "patrol" ? "Patrol Guard" : "Sentinel Guard");

      const guardInfo = {
        ...rawGuard,
        id: memberId,
        memberId,
        adminId,
        role: designation,
        designation,
        loginType: formData.loginType,
        guardLoginType: formData.loginType,
      };

      if (formData.remember) {
        localStorage.setItem(
          "guard_remembered_username",
          formData.username.trim()
        );
        localStorage.setItem("guard_remembered_type", formData.loginType);
      } else {
        localStorage.removeItem("guard_remembered_username");
        localStorage.removeItem("guard_remembered_type");
      }

      clearOldAdminLoginData();

      localStorage.setItem("token", token);
      localStorage.setItem("authToken", token);
      localStorage.setItem("guardToken", token);

      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("isGuardLoggedIn", "true");

      localStorage.setItem("loginType", formData.loginType);
      localStorage.setItem("guardLoginType", formData.loginType);

      localStorage.setItem("role", designation);
      localStorage.setItem("designation", designation);

      localStorage.setItem("userData", JSON.stringify(guardInfo));
      localStorage.setItem("guardData", JSON.stringify(guardInfo));

      localStorage.setItem("memberId", memberId || "");
      localStorage.setItem("adminId", adminId || "");
      localStorage.setItem(
        "guardName",
        guardInfo.fullName || guardInfo.name || ""
      );
      localStorage.setItem("guardEmail", guardInfo.email || "");

      showStatus("Login successful! Getting location...", "success");

      try {
        await Promise.race([
          startGpsOnLogin(token),
          new Promise((resolve) => setTimeout(resolve, 3000)),
        ]);
      } catch (gpsError) {
        console.error("[GuardLogin] GPS race error:", gpsError);
      }

      window.location.href = "/dashboard";
    } catch (error) {
      console.error("[GuardLogin] Login error:", error);
      showStatus("Network error. Please try again.", "error");
      setLoading(false);
    }
  };

  const getStatusStyles = () => {
    if (status.type === "success") {
      return "border border-emerald-200 bg-emerald-50 text-emerald-700";
    }

    if (status.type === "error") {
      return "border border-red-200 bg-red-50 text-red-700";
    }

    return "border border-blue-200 bg-blue-50 text-blue-700";
  };

  const getStatusIcon = () => {
    if (status.type === "success") return "✓";
    if (status.type === "error") return "⚠";
    return "ℹ";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-800 to-slate-700 flex items-center justify-center p-6">
      <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="bg-gradient-to-br from-[#071426] to-[#12355B] px-8 py-10 text-center text-white">
          <div className="mx-auto mb-4 h-24 w-24 overflow-hidden rounded-full bg-white p-2 shadow-lg">
            <img
              src={logo}
              alt="Guard Login Logo"
              className="h-full w-full rounded-full object-cover"
            />
          </div>

          <h1 className="text-3xl font-bold">Guard Login</h1>

          <p className="mt-2 text-sm text-slate-200">
            Login to Patrol or Sentinel guard account
          </p>
        </div>

        <div className="px-8 py-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Login Type
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label
                  className={`cursor-pointer rounded-xl border px-4 py-3 text-center text-sm font-semibold transition ${
                    formData.loginType === "sentinel"
                      ? "border-[#12355B] bg-blue-50 text-[#12355B]"
                      : "border-slate-300 bg-slate-50 text-slate-600"
                  }`}
                >
                  <input
                    type="radio"
                    name="loginType"
                    value="sentinel"
                    checked={formData.loginType === "sentinel"}
                    onChange={handleChange}
                    className="hidden"
                  />
                  Sentinel
                </label>

                <label
                  className={`cursor-pointer rounded-xl border px-4 py-3 text-center text-sm font-semibold transition ${
                    formData.loginType === "patrol"
                      ? "border-[#12355B] bg-blue-50 text-[#12355B]"
                      : "border-slate-300 bg-slate-50 text-slate-600"
                  }`}
                >
                  <input
                    type="radio"
                    name="loginType"
                    value="patrol"
                    checked={formData.loginType === "patrol"}
                    onChange={handleChange}
                    className="hidden"
                  />
                  Patrol
                </label>
              </div>
            </div>

            <div>
              <label
                htmlFor="username"
                className="mb-2 block text-sm font-semibold text-slate-700"
              >
                Username / Member ID / Email
              </label>

              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                  👤
                </span>

                <input
                  type="text"
                  id="username"
                  name="username"
                  placeholder="Enter guard username"
                  autoComplete="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-slate-300 bg-slate-50 py-3 pl-10 pr-4 text-sm text-slate-800 outline-none transition focus:border-[#12355B] focus:bg-white focus:ring-4 focus:ring-blue-100"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-2 block text-sm font-semibold text-slate-700"
              >
                Password
              </label>

              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                  🔒
                </span>

                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  placeholder="Enter password"
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-slate-300 bg-slate-50 py-3 pl-10 pr-12 text-sm text-slate-800 outline-none transition focus:border-[#12355B] focus:bg-white focus:ring-4 focus:ring-blue-100"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-lg text-slate-500"
                >
                  {showPassword ? "🙈" : "👁"}
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  name="remember"
                  checked={formData.remember}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-slate-300 text-[#12355B] focus:ring-[#12355B]"
                />
                Remember me
              </label>

              <a
                href="#"
                className="text-sm font-semibold text-[#12355B] hover:underline"
              >
                Forgot Password?
              </a>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-[#071426] to-[#12355B] px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-80"
            >
              {loading && (
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"></span>
              )}

              {loading ? "Logging in..." : "Login as Guard"}
            </button>
          </form>

          {status.visible && (
            <div
              className={`mt-5 flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium ${getStatusStyles()}`}
            >
              <span>{getStatusIcon()}</span>
              <span>{status.message}</span>
            </div>
          )}

          <div className="mt-6 border-t border-slate-200 pt-5 text-center">
            <div className="space-y-2 text-sm text-slate-600">
              <a
                href="mailto:eblackdogsupport@nissiinfotech.in"
                className="block hover:text-[#12355B]"
              >
                eblackdogsupport@nissiinfotech.in
              </a>

              <a
                href="https://www.eblackdog.com"
                target="_blank"
                rel="noreferrer"
                className="block hover:text-[#12355B]"
              >
                www.eblackdog.com
              </a>

              <p className="font-semibold text-slate-700">
                90037-44644 / 98422-44488
              </p>
            </div>

            <p className="mt-3 text-xs text-slate-500">
              Owned by EBlack Dog Patrol Management System
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}